import { useCallback } from 'react'
// Material
import { Box, InputBase } from '@mui/material'
import { styled } from '@mui/material/styles'
// Common
import { AlternateEmailIcon } from '$common/icons/alternateEmail'
import { LengthVerifier } from '$common/input/length-verifier'
// Components
import { Alert } from './alert'
import { t } from '$i18n'

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  border: `2px solid ${theme.palette.secondary.main}`,
  borderRadius: 8,
  height: 40,
  caretColor: theme.palette.secondary.main,
  color: theme.palette.semiBlack,
  '&.Mui-error': {
    borderColor: theme.palette.error.main,
  },
}))

const StyledBox = styled(Box)(({ theme }) => ({
  height: '100%',
  minWidth: '44px',
  backgroundColor: theme.palette.veryVeryLightGray,
  marginRight: '8px',
  borderRadius: '8px 0 0 8px',
  borderColor: 'transparent',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  // TODO: REPLACE WITH THEME PALETTE
  borderRight: '2px solid #E4E4E4',
}))

const Adornment = () => (
  <StyledBox>
    <AlternateEmailIcon
      sx={{
        fontSize: '16px',
      }}
    />
  </StyledBox>
)

export type UsernameStatus =
  | 'AVAILABLE'
  | 'NOT_AVAILABLE'
  | 'INVALID'
  | 'LESS_THAN_5_CHAR'
  | 'NOT_START_WITH_LETTER'

interface Props {
  value: string
  setValue: (newValue: string) => unknown
  status?: UsernameStatus
  maxLength?: number
  showMaxLength?: boolean
}

const i18nMap: Record<UsernameStatus, string> = {
  AVAILABLE: t('validation.username.isAvailable'),
  INVALID: t('validation.username.notSupportedChars'),
  LESS_THAN_5_CHAR: t('validation.username.leastChars'),
  NOT_START_WITH_LETTER: t('validation.username.notStartWithLetter'),
  NOT_AVAILABLE: t('validation.username.exists'),
}

export function UsernameInput({
  value,
  setValue,
  status,
  maxLength,
  showMaxLength,
}: Props) {
  const alert = status ? i18nMap[status] : null
  const variant = status === 'AVAILABLE' ? 'success' : 'error'

  const handleInputChange = useCallback(
    (newValue: string) => {
      if (!maxLength || newValue.length <= maxLength) setValue(newValue)
    },
    [setValue],
  )

  return (
    <>
      <StyledInputBase
        dir="ltr"
        autoFocus
        fullWidth
        startAdornment={<Adornment />}
        value={value}
        onChange={e => handleInputChange(e.target.value)}
      />
      {showMaxLength && maxLength && (
        <Box sx={{ marginTop: '8px' }}>
          <LengthVerifier value={value} maxLength={maxLength} />
        </Box>
      )}
      {alert && <Alert text={alert} variant={variant} />}
    </>
  )
}

UsernameInput.defaultProps = {
  maxLength: null,
  showMaxLength: false,
}
